// src/FileGenerator/AdminFileGenerator/OnboardingReport.js
import jsPDF from "jspdf";
import "jspdf-autotable";

const generateOnboardingPDF = (res, meta = {}) => {
  const data = res?.data ?? {};
  const isCompleted = data?.isCompleted;   
  const doc = new jsPDF();

  const { name = "N/A", email = "N/A" } = meta;

  // ---------------- TITLE ----------------
  doc.setFontSize(18);
  doc.text("Employee Onboarding Report", 14, 22);

  doc.setLineWidth(0.5);
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 28, 200, 28);

  // ---------------- HEADER ----------------
  doc.setFontSize(11);

  doc.text(`Employee: ${name}`, 14, 38);
  doc.text(`Email: ${email}`, 14, 45);
  doc.text(
    `Status: ${isCompleted ? "COMPLETED" : "IN PROGRESS"}`,
    14,
    52
  );

  doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 14, 59);

  // ---------------- TASK LISTS WITH DESCRIPTIONS ----------------
  const taskSections = [
    {
      title: "Tasks for HR Administrator",
      tasks: [
        {
          key: "welcomeEmail",
          label: "Welcome Email",
          description: "Share the good news! Send a welcome email introducing your new hire.",
        },
        {
          key: "mentorAssignment",
          label: "Mentor Assignment",
          description: "Assign your new hire a mentor and/or onboarding buddy.",
        },
        {
          key: "recurringOneOnOneMeetings",
          label: "Recurring One-on-One Meetings",
          description: "Invite your new employee to recurring one-on-one meetings to provide regular check-ins.",
        },
        {
          key: "firstDayMeetings",
          label: "First Day Meetings",
          description: "Schedule your new hire's first day meetings, prioritizing intro calls with colleagues who will work closely with the new hire.",
        },
        {
          key: "welcomeMeeting",
          label: "Welcome Meeting",
          description: "Coordinate a welcome meeting or phone call to kick off your new hire's first day.",
        },
        {
          key: "welcomeLunch",
          label: "Welcome Lunch",
          description: "Set up a welcome lunch with the new hire's team, if appropriate.",
        },
        {
          key: "businessIntroduction",
          label: "Business Introduction",
          description: "Give the new employee an introduction to the business, including how their role fits in and any performance expectations you'll have for their onboarding.",
        },
        {
          key: "officeTour",
          label: "Office Tour",
          description: "Take your new hire on a tour of the office.",
        },
      ],
    },
    {
      title: "Mentor or Onboarding Buddy Checklist",
      tasks: [
        {
          key: "regularCheckIns",
          label: "Regular Check-ins",
          description: "Schedule regular check-ins throughout the first two weeks.",
        },
        {
          key: "shadowingOpportunities",
          label: "Shadowing Opportunities",
          description: "Invite new hires to shadow relevant meetings or projects.",
        },
        {
          key: "adHocSupport",
          label: "Ad-hoc Support",
          description: "Be responsive to ad hoc questions throughout the day!",
        },
      ],
    },
    {
      title: "Required Onboarding Documents for New Hires",
      tasks: [
        {
          key: "benefitsEnrollment",
          label: "Benefits Enrollment",
          description: "Benefits enrollment paperwork",
        },
        {
          key: "emergencyContactInfo",
          label: "Emergency Contact Info",
          description: "Emergency contact information",
        },
        {
          key: "employeeHandbook",
          label: "Employee Handbook",
          description: "Employee handbook featuring all company policies",
        },
        {
          key: "directDepositInfo",
          label: "Direct Deposit Info",
          description: "Direct deposit and payroll information",
        },
        {
          key: "employmentEligibilityForm",
          label: "Employment Eligibility Form",
          description: "i-9 form (employment eligibility verification)",
        },
        {
          key: "ndaOrNonCompete",
          label: "NDA / Non-Compete",
          description: "non-disclosure agreement, non-compete, or confidentiality agreement (if applicable)",
        },
        {
          key: "stateTaxWithholdingForm",
          label: "State Tax Withholding",
          description: "State tax withholding form (varies by location)",
        },
        {
          key: "federalTaxWithholdingForm",
          label: "Federal Tax Form",
          description: "w-4 form (employee's withholding certificate for federal taxes) or W-9 for contractors",
        },
      ],
    },
  ];

  // ---------------- TABLES FOR EACH SECTION ----------------
  let currentY = 70;

  taskSections.forEach((section) => {
    // Add section title
    doc.setFontSize(14);
    doc.setTextColor(61, 192, 15); // Green color for section titles
    doc.text(section.title, 14, currentY);
    currentY += 10;

    // Create table data for this section
    const tableData = section.tasks.map((task) => {
      const status = data?.[task.key];
      
      // Truncate description if too long
      let description = task.description;
      if (description.length > 70) { // Increased from 60 to 70 since we're making the description column wider
        description = description.substring(0, 67) + "...";
      }
      
      return [
        task.label,
        description,
        status ? "Completed" : "Pending",
      ];
    });

    // Add table for this section
    doc.autoTable({
      startY: currentY,
      head: [["Task", "Description", "Status"]],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [50, 50, 50],
        textColor: 255,
        halign: "center",
        fontSize: 9,
        fontStyle: "bold",
        cellPadding: 3,
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { 
          cellWidth: 45,
          fontStyle: "bold",
          fillColor: [240, 240, 240],
        }, 
        1: { 
          cellWidth: 110, // Increased from 100 to 110
        }, 
        2: { 
          cellWidth: 30, // Reduced from 40 to 30
          halign: "center",
          fillColor: [240, 240, 240],
        }, 
      },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap',
      },
      tableWidth: 185, // Keep the same total width
      margin: { left: 14, right: 14 },
      didDrawCell: function(data) {
        if (data.section === 'body' && data.column.index === 2) {
          const status = data.cell.raw;
          if (status === "Completed") {
            doc.setTextColor(0, 128, 0);
          } else {
            doc.setTextColor(255, 0, 0);
          }
        }
      },
    });

    // Update currentY for next section
    currentY = doc.lastAutoTable.finalY + 15;
    
    // Add a new page if needed
    if (currentY > 250) {
      doc.addPage();
      currentY = 20;
    }
  });

  // ---------------- SAVE ----------------
  doc.save(`onboarding_report_${data.jobApplicantId || "employee"}.pdf`);
};

export default generateOnboardingPDF;